import axiosInstance from "../Axios";

// Generates the requested compliance document (e.g. PT monthly statement,
// LWF Form A/B) and returns it as a downloadable file.
function generateCompliance(payload) {
  try {
    return axiosInstance.post("/compliance/generate", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating compliance document:", error);
    throw error;
  }
}

// Generates the LWF statutory Form A/B (state letterhead template with
// employee/employer contribution rates) - the "newer" LWF generation method.
function generateLwfStatutoryForm(payload) {
  try {
    return axiosInstance.post("/compliance/generate-lwf-form", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating LWF statutory form:", error);
    throw error;
  }
}

// Generates the Form XVII - Register of Wages statutory Excel download for
// one payroll month + state. Sits alongside generateLwfStatutoryForm - same
// /compliance router, same blob-download contract. Minimum Wages needs no
// equivalent new function here: it reuses generateCompliance() above as-is,
// just with form: "mw" in the payload.
function generateFormXVII(payload) {
  try {
    return axiosInstance.post("/compliance/generate-form-xvii", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating Form XVII register of wages:", error);
    throw error;
  }
}

// Generates the "Statement of Profession Tax" (letterhead + rate summary +
// sign-off) for one payroll month and state, returned as a downloadable file.
function generateProfessionTaxStatement(payload) {
  try {
    return axiosInstance.post("/compliance/pt-statement", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating profession tax statement:", error);
    throw error;
  }
}

// Generates the Form 24Q quarterly TDS statement (one sheet per month in the
// selected financial-year quarter) as a downloadable file. Unlike PT/LWF,
// this takes no state - TDS on salaries is filed centrally.
function generateForm24Q(payload) {
  try {
    return axiosInstance.post("/compliance/form24q", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating Form 138(24Q) statement:", error);
    throw error;
  }
}

// Generates the Form A - Muster Roll (Maternity Benefit Act) statutory
// Excel download for one employee + one payroll month.
function generateMaternityFormA(payload) {
  try {
    return axiosInstance.post("/compliance/generate-maternity-form-a", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating Form A - Muster Roll:", error);
    throw error;
  }
}
// Fetches every employee (active + inactive) for the Form 12B "Employees"
// picker - unlike the other compliance forms, Form 12B is generated only
// for whichever specific employees are selected, so it needs the full list
// to choose from rather than a state/status filter, but the list is still
// narrowed to the State already selected on the Compliance page (`stateId`),
// the same optional param `getUsersByStatus` already sends for Form C/F/15.
function getForm12BEmployees(stateId) {
  try {
    return axiosInstance.get("/compliance/form12b/employees", {
      params: { stateId: stateId || undefined },
    });
  } catch (error) {
    console.error("Error fetching employees for Form 12B:", error);
    throw error;
  }
}

// Generates Form 12B (statement of salary/TDS particulars, one sheet per
// employee) for one calendar year and the selected employees, as a
// downloadable file.
function generateForm12B(payload) {
  try {
    return axiosInstance.post("/compliance/form12b", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating Form 12B:", error);
    throw error;
  }
}

// Fetches active states for the "State" dropdown (India by default).
function getStates(countryId) {
  try {
    return axiosInstance.get("/compliance/states", {
      params: countryId ? { countryId } : undefined,
    });
  } catch (error) {
    console.error("Error fetching states:", error);
    throw error;
  }
}

// Fetches all compliance forms configured for a state, grouped by
// compliance type, e.g. { PT: [...], LWF: [...] }.
function getComplianceFormsByState(stateId) {
  try {
    return axiosInstance.get(`/compliance/forms/${stateId}`);
  } catch (error) {
    console.error("Error fetching compliance forms:", error);
    throw error;
  }
}

// Fetches the filing frequency configured in pms_compliance_master for a
// compliance type (PT/LWF) in a state, e.g. { frequency: "3", frequencyLabel: "Quarterly" }.
function getComplianceFrequency(type, stateId) {
  try {
    return axiosInstance.get(`/compliance/frequency/${type}/${stateId}`);
  } catch (error) {
    console.error("Error fetching compliance frequency:", error);
    throw error;
  }
}

// Generates Form C - Bonus Paid to Employees (Payment of Bonus Act -
// Central) for one financial year (April-March), for all applicable
// employees or a single selected one, as a downloadable file.
function generateBonusFormC(payload) {
  try {
    return axiosInstance.post("/compliance/generate-bonus-form-c", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating Bonus Form C:", error);
    throw error;
  }
}

// Generates Form D - Annual Return - Bonus Paid to Employees (Payment of
// Bonus Act - Central) for one financial year, as a downloadable file.
function generateBonusFormD(payload) {
  try {
    return axiosInstance.post("/compliance/generate-bonus-form-d", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating Bonus Form D:", error);
    throw error;
  }
}

// Generates Form F - Nomination (Payment of Gratuity Act - Central) - one
// employee's form, or one complete Form F per active employee (one workbook,
// one sheet each) when `all_employees` is sent - as a downloadable file.
function generateGratuityFormF(payload) {
  try {
    return axiosInstance.post("/compliance/generate-gratuity-form-f", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating Gratuity Form F:", error);
    throw error;
  }
}

// Generates Form 15 - Register of Leave with Wages (Factories Act, Section
// 79) for one calendar year (Jan-Dec - filed year-wise, not financial-year-
// wise) - a single employee's register when employee_id is sent, or one
// complete register per active employee (one workbook, one sheet each) when
// it isn't. Common for every state - the backend derives each employee's own
// real state, never Delhi-specific.
function generateForm15(payload) {
  try {
    return axiosInstance.post("/compliance/generate-form-15", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating Form 15 - Register of Leave with Wages:", error);
    throw error;
  }
}

// Generates the Apprentices Act, 1961 - Apprentice/Training Report (one row
// per apprentice/trainee/intern per month in the selected From Date/To Date
// range) as a 2-sheet Excel download (employee-wise monthly detail +
// summary) - the exact same "Generate -> direct file download" contract
// generateForm15() above uses, not a separate JSON/on-screen flow.
function generateApprenticesAct(payload) {
  try {
    return axiosInstance.post("/compliance/generate-apprentices-act", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating the Apprentices Act report:", error);
    throw error;
  }
}

// Shops & Establishment Act forms themselves come back from the existing
// getComplianceFormsByState(stateId) above - compliance_type = "SHOPS" is
// just another bucket in that same grouped response, exactly like PT/LWF/
// MW/LLF - so there's no separate "list Shops forms" call here.

// Generates the blank Shops & Establishment report template (Phase 1 - see
// task scope: headers only, no data rows yet) for the selected form, as a
// downloadable file - same blob-download contract every other Generate
// button above already uses.
function generateShopsEstablishmentForm(payload) {
  try {
    return axiosInstance.post("/compliance/generate-shops-establishment-form", payload, {
      responseType: "blob",
    });
  } catch (error) {
    console.error("Error generating the Shops & Establishment report:", error);
    throw error;
  }
}

export {
  generateCompliance,
  generateLwfStatutoryForm,
  generateFormXVII,
  generateProfessionTaxStatement,
  generateForm24Q,
  generateMaternityFormA,
  getForm12BEmployees,
  generateForm12B,
  getStates,
  getComplianceFormsByState,
  getComplianceFrequency,
  generateBonusFormC,
  generateBonusFormD,
  generateGratuityFormF,
  generateForm15,
  generateApprenticesAct,
  generateShopsEstablishmentForm,
};
